import React from 'react';
import { Pagination, PaginationProps } from 'common/components';

import styles from './Table.module.scss';
import { LoaderCircle } from 'lucide-react';

export interface TableColumn<T = any> {
    key: string;
    title: string | React.ReactNode;
    dataIndex?: keyof T;
    render?: (value: any, record: T, index: number) => React.ReactNode;
    width?: string | number;
    align?: 'left' | 'center' | 'right';
    className?: string;
}

export type TableVariant = 'primary' | 'secondary';

export interface TablePaginationConfig extends PaginationProps {
    enabled: boolean;
}

export interface TableProps<T = any> {
    data: T[];
    columns: TableColumn<T>[];
    isLoading?: boolean;
    emptyText?: string;
    className?: string;
    rowKey?: keyof T | ((record: T, index: number) => string | number);
    onRowClick?: (record: T, index: number) => void;
    rowClassName?: (record: T, index: number) => string;
    showHeader?: boolean;
    size?: 'small' | 'medium' | 'large';
    variant?: TableVariant;
    bordered?: boolean;
    striped?: boolean;
    hoverable?: boolean;
    // Pagination configuration
    pagination?: TablePaginationConfig;
}

export const Table = <T extends Record<string, any>>({
    data,
    columns,
    isLoading = false,
    emptyText = 'No data available',
    className = '',
    rowKey = 'id',
    onRowClick,
    rowClassName,
    showHeader = true,
    size = 'medium',
    variant = 'primary',
    bordered = false,
    striped = false,
    hoverable = false,
    pagination,
}: TableProps<T>) => {
    const getRowKey = (record: T, index: number): string | number => {
        if (typeof rowKey === 'function') {
            return rowKey(record, index);
        }
        return record[rowKey] || index;
    };

    const getRowClassName = (record: T, index: number): string => {
        const baseClass = styles.tableRow;
        const stripedClass = striped && index % 2 === 1 ? styles.striped : '';
        const hoverableClass = hoverable ? styles.hoverable : '';
        const customClass = rowClassName ? rowClassName(record, index) : '';
        const clickableClass = onRowClick ? styles.clickable : '';

        return [baseClass, stripedClass, hoverableClass, customClass, clickableClass].filter(Boolean).join(' ');
    };

    const getCellClassName = (column: TableColumn<T>): string => {
        const baseClass = styles.tableCell;
        const alignClass = column.align ? styles[`align-${column.align}`] : '';
        const customClass = column.className || '';

        return [baseClass, alignClass, customClass].filter(Boolean).join(' ');
    };

    return (
        <div className={`${styles.tableContainer} ${className}`}>
            <div className={styles.tableWrapper}>
                <table
                    className={`${styles.table} ${styles[`size-${size}`]} ${styles[`variant-${variant}`]} ${bordered ? styles.bordered : ''}`}
                >
                    {showHeader && (
                        <thead>
                            <tr className={styles.tableHeader}>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className={getCellClassName(column)}
                                        style={{ width: column.width }}
                                    >
                                        {column.title}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        {data.length > 0 ? (
                            data.map((record, index) => (
                                <tr
                                    key={getRowKey(record, index)}
                                    className={getRowClassName(record, index)}
                                    onClick={() => onRowClick?.(record, index)}
                                >
                                    {columns.map((column) => (
                                        <td key={column.key} className={getCellClassName(column)}>
                                            {column.render
                                                ? column.render(
                                                      column.dataIndex ? record[column.dataIndex] : record,
                                                      record,
                                                      index
                                                  )
                                                : column.dataIndex
                                                  ? record[column.dataIndex]
                                                  : ''}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className={styles.emptyMessage}>
                                    {emptyText}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {isLoading && (
                    <div data-testid='loading-overlay' className={styles.loadingOverlay}>
                        <LoaderCircle size={26} className={styles.spinner} color='var(--primary-purple)' />
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination?.enabled && (
                <div className={styles.paginationContainer}>
                    <Pagination {...pagination} />
                </div>
            )}
        </div>
    );
};

export default Table;
